import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { detailKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../../components/CashFigures';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { CountSheet, useCountSheet } from './CountSheet';
import { dropKey } from './TheDrop';

/**
 * PULLING THE DRAWER: the owner counts a register himself, mid-shift, and nobody's evening stops.
 *
 * His words: *"I should be able to pull the drawer at any time whether we are open or closed and the
 * system should report how much is expected in the drawer"* — and, on what should happen when he
 * does it during service, *"record the count, leave the session running, with an option to adjust
 * the drawer."*
 *
 * Everything on this panel is his. **No part of it is a bartender's workflow.** He is not opening a
 * shift, not dropping a pouch, not closing anything: he walks up to a register that is serving, counts
 * what is in it, and writes down what he found. The shift goes on afterwards exactly as it was.
 *
 * The other half of "whether we are open or closed" — a register with no shift open on it at all — is
 * `PullADrawer.tsx`, on the register screen. It is the same gesture against a different subject: the
 * drawer as it stands, rather than the money on a shift.
 *
 * ## Three deliberate choices, each of which was the wrong way round in an earlier draft
 *
 * **1. The count is typed as NOTES, never as a total.** Over and short has never been a figure
 * anybody types in this system, and the moment a box on a screen accepts one it is a figure somebody
 * can make agree. Quantities of each note go in and the server multiplies; the arithmetic is done
 * where the money rules live. The sheet itself is `CountSheet.tsx` — the one used everywhere he
 * counts money, which this file used to carry a private fork of.
 *
 * **2. What the drawer should be holding is NOT shown until the count is submitted.** He is
 * genuinely allowed to see it — the blind-count rule protects the person the variance would be
 * attributed to, and that is the bartender rather than him — but a figure on screen while somebody
 * counts is a figure the counting drifts towards. So it arrives with the answer.
 *
 * **3. Adjusting the drawer is a SECOND press, and it is taken on the count already recorded.**
 * A tick-box saying "and move the drawer onto what I count" would ask him to decide before knowing
 * by how much, which is not a decision anybody can take honestly. It also takes a reason, and the
 * reason is not optional: a corrected drawer that erases the evidence it was ever wrong is worse
 * than no audit at all.
 *
 * ## THE SECOND PRESS SENDS A COUNT ID, NOT THE SHEET AGAIN
 *
 * It used to re-submit the same denomination lines with `adjustDrawer` set, on the reasoning that
 * the expectation the adjustment is measured against must be the one in force at the moment it is
 * applied. Both halves of that went wrong, and the bar trading through the thirty seconds is why.
 *
 * The sheet is an observation of 10:31. Measured again against the expectation as it stands at
 * 10:31:30, a ten-dollar cash sale in between makes those two figures answers about different
 * drawers: the correction lands the register on what was counted BEFORE the sale, and it reads ten
 * dollars over at close — against the one person behind the bar. And it wrote a second `adhoc` count
 * row with the same figures at the same instant, so his count sheet showed him counting the same
 * money twice, both readings short: one shortage reported as two.
 *
 * `POST /admin/v1/cash/counts/{id}/adjust` moves the drawer by the difference **that count found**,
 * applied relatively, so it composes with the sale instead of throwing it away — and there is one
 * row, carrying both the count and the decision taken on it.
 */

/** What the server answers when the count lands: what was expected, what was there, the difference. */
interface PullResult {
  cashCountId: string;
  expected: CashPair;
  counted: CashPair;
  variance: CashPair;
  allThere: boolean;
  adjustedTheDrawer: boolean;
  checkedByName: string | null;
  checkedAt: string;
}

/** And when the drawer is moved onto it: by how much, and what it is expected to hold now. */
interface MovedResult {
  cashCountId: string;
  countedAgainst: CashPair;
  counted: CashPair;
  movedBy: CashPair;
  expectedNow: CashPair;
  reason: string;
}

export function PullTheDrawer({
  drawerSessionId,
  status,
  onRecorded,
}: {
  drawerSessionId: string;
  status: 'open' | 'closed';
  onRecorded: () => void;
}) {
  const locationId = useLocationId();
  /*
   * Undefined for the moment the location is still loading, and the query is then left off entirely
   * rather than sent empty — the API's own default is this bar, and an empty `locationId=` would be
   * a request for a site that does not exist.
   */
  const where = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
  const queryClient = useQueryClient();
  const sheet = useCountSheet();
  const [note, setNote] = useState('');
  const [result, setResult] = useState<PullResult | null>(null);
  const [moved, setMoved] = useState<MovedResult | null>(null);
  const [reason, setReason] = useState('');

  /**
   * EVERY FIGURE ON THIS PAGE THAT THE COUNT JUST MOVED, and there is a third one.
   *
   * Moving the drawer changes what it is expected to hold, and the drop is derived from exactly that
   * — so the panel below this one went on printing figures from before the adjustment. Live, that
   * read as *"Should be in it L 10,000.00"* four inches above *"This drawer is below the float it
   * opens with by L 1,988.00"*: two statements about the same register, on the same screen, disagreeing
   * by ten thousand lempiras, one of them four minutes stale. A page whose whole argument is that
   * two independent sums have to agree cannot leave one of them un-refreshed.
   */
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: detailKey('/admin/v1/cash/sessions', drawerSessionId) });
    queryClient.invalidateQueries({
      queryKey: detailKey('/admin/v1/cash/sessions/summary', drawerSessionId),
    });
    queryClient.invalidateQueries({ queryKey: dropKey(drawerSessionId, locationId) });
    onRecorded();
  };

  const record = useMutation({
    mutationFn: () =>
      rawApi<PullResult>('POST', `/admin/v1/cash/sessions/${drawerSessionId}/audit${where}`, {
        denominationLines: sheet.lines(),
        note: note || null,
        adjustDrawer: false,
      }),
    onSuccess: (answer) => {
      setResult(answer);
      notifyDone(
        'Count recorded',
        answer.allThere
          ? 'The money is all there. The shift is still running.'
          : 'The difference is recorded against this drawer. The shift is still running.',
      );
      refresh();
    },
    onError: (error) => notifyError(error, 'record what you counted'),
  });

  /**
   * The second press. It names the count that is already on record and sends the reason with it —
   * the server refuses the move without one, and moves the drawer by the difference that count
   * found rather than by a difference worked out again from the same notes half a minute later.
   */
  const adjust = useMutation({
    mutationFn: () =>
      rawApi<MovedResult>('POST', `/admin/v1/cash/counts/${result?.cashCountId}/adjust`, { reason }),
    onSuccess: (answer) => {
      setMoved(answer);
      setReason('');
      notifyDone(
        'Drawer moved',
        'The rest of the shift is measured against what you counted. The difference stays on record.',
      );
      refresh();
    },
    onError: (error) => notifyError(error, 'move the drawer onto what you counted'),
  });

  const startAgain = () => {
    sheet.clear();
    setNote('');
    setReason('');
    setResult(null);
    setMoved(null);
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={5}>Pull this drawer</Title>
          {/*
            WHICH MONEY HE IS BEING ASKED TO COUNT, and it is not the same money on the two states.

            "Count what is actually in the register" is true of a shift that is running. On a shift that
            was counted off last night it is a trap in one sentence: the notes in that register now belong
            to whoever is behind the bar tonight, and counting them here would attribute tonight's
            cash to a shift that ended thirty-six hours ago and be measured against an expectation
            frozen at its close. The panel said so — but only in a line printed AFTER the count had
            been submitted, which is the wrong end of the gesture to put a warning at.
          */}
          <Text size="sm" c="dimmed" mt={2} mb="sm">
            {status === 'open'
              ? 'Count what is actually in the register and record it. The shift keeps running — nobody is interrupted and nothing is closed. '
              : 'This shift was counted off, so what you count here is the money that came off it — a pouch not yet dropped, a register not yet reset — and not what is in that register during the current business day. '}
            What the drawer should be holding is shown once you have submitted the count, so the figure on the
            screen cannot lead the counting.
          </Text>
        </div>
        {result && (
          <Button size="compact-xs" variant="subtle" onClick={startAgain}>
            Count it again
          </Button>
        )}
      </Group>

      {!result && (
        <>
          <CountSheet
            notes={sheet.notes}
            loading={sheet.loading}
            /* A failed denominations read leaves the sheet rowless and the submit inert — no wrong
               count can post — but without this the modal blames the bar's configuration for it. */
            error={sheet.error}
            sheet={sheet.sheet}
            onChange={(key, value) => sheet.setSheet({ ...sheet.sheet, [key]: value })}
          />

          <Textarea
            mt="md"
            label="Note"
            description="Anything worth remembering about this count — who was on, what the register looked like."
            autosize
            minRows={1}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />

          <Group mt="md" justify="space-between">
            {/* The sheet's own total, live. Not the figure that gets stored — the server adds the
                notes up itself — but the one a person needs while they are still holding the cash. */}
            <Group gap={6}>
              <Text size="sm" c="dimmed">
                On the sheet
              </Text>
              <MoneyPair pair={sheet.running} fw={700} />
            </Group>
            <Button
              disabled={!sheet.anythingTyped || record.isPending}
              loading={record.isPending}
              onClick={() => record.mutate()}
            >
              Record what I counted
            </Button>
          </Group>
        </>
      )}

      {result && (
        <>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Card withBorder>
              <Text size="xs" c="dimmed">
                Should have been there
              </Text>
              <MoneyPair pair={result.expected} fw={700} />
            </Card>
            <Card withBorder>
              <Text size="xs" c="dimmed">
                You counted
              </Text>
              <MoneyPair pair={result.counted} fw={700} />
            </Card>
            <Card withBorder>
              <Text size="xs" c="dimmed">
                Difference
              </Text>
              <OverShortPair pair={result.variance} />
            </Card>
          </SimpleGrid>

          {result.allThere ? (
            <Alert color="green" mt="md">
              The money is all there. Nothing else to do — the count is on the drawer with your name and the
              time against it.
            </Alert>
          ) : moved ? (
            /*
              What the drawer is expected to hold NOW, read back off the server rather than worked
              out here. It is not the figure he counted: the register has been trading, and the
              correction composed with whatever came over the counter while he was deciding. Printing
              the counted total instead would be a fourth figure on this screen that nothing agrees
              with by the time he looks at the drop panel below.
            */
            <Alert color="blue" mt="md">
              The drawer has been moved onto what you counted, and the rest of the shift is measured against{' '}
              <strong>
                {formatMoney(moved.expectedNow.usdMinor, 'USD')} ·{' '}
                {formatMoney(moved.expectedNow.hnlMinor, 'HNL')}
              </strong>
              . The difference above stays on the count sheet with your reason beside it — counting the drawer
              off for the business day will not swallow it.
            </Alert>
          ) : (
            <Alert color="orange" mt="md">
              <Text size="sm" fw={600} mb={4}>
                Recorded. The shift is still running.
              </Text>
              <Text size="sm">
                Leave it here and the bartender is counted off for this business day against the figure that
                is already {formatMoney(Math.abs(result.variance.usdMinor), 'USD')} ·{' '}
                {formatMoney(Math.abs(result.variance.hnlMinor), 'HNL')} out — they will sign for this
                difference as well as for the rest of their evening. Move the drawer onto what you counted and
                the difference stays here, on this count, instead.
              </Text>
            </Alert>
          )}

          {!result.allThere && !moved && status === 'open' && (
            <Stack gap="xs" mt="md">
              <Textarea
                label="Why is the drawer being moved?"
                description="Required, and it is the only account anybody will have of this later. Say what you think happened."
                autosize
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button
                  variant="light"
                  disabled={reason.trim().length < 4 || adjust.isPending}
                  loading={adjust.isPending}
                  onClick={() => adjust.mutate()}
                >
                  Move the drawer onto this count
                </Button>
              </Group>
            </Stack>
          )}

          {!result.allThere && status === 'closed' && (
            <Text size="sm" c="dimmed" mt="sm">
              This drawer has already been counted off, so what it came up over or short cannot move — that
              figure is what the night was reported on. The count above is recorded against it either way.
            </Text>
          )}
        </>
      )}
    </Paper>
  );
}

/** A count that moved the drawer, flagged on the count sheet with the words that justified it. */
export function AdjustedBadge({ reason }: { reason: string | null }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Badge size="xs" color="blue" variant="light">
        Drawer moved
      </Badge>
      {reason && (
        <Text size="xs" c="dimmed" lineClamp={2}>
          {reason}
        </Text>
      )}
    </Group>
  );
}
